/**
 * useLiveMatchLineup
 *
 * On-court / bench lineup per side for sports with substitutions (advanced mode).
 *
 * - Starts from the lineup saved in match_config.lineup (refresh / second device),
 *   otherwise from member types (primary on court, substitute/reserve on bench).
 * - Substitutions are applied optimistically, saved with PUT /matches/:id/lineup,
 *   and reverted if the server refuses them.
 * - Other devices follow along through "lineup_update" match events.
 */
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { apiClient } from '../../../../services/api/apiService';
import { getSubstitutionRules } from '../../../../utils/sportRules';
import {
  buildInitialLineup,
  reconcileLineup,
  canSubstitute,
  applySubstitution,
  resetForNewSet,
} from '../utils/lineupUtils';

const SIDES = ['participant1', 'participant2'];
const EMPTY = { participant1: null, participant2: null };

const useLiveMatchLineup = ({ match, sportName, p1Players, p2Players, active, canEdit, matchEvents }) => {
  const matchConfig = match?.matchConfig || match?.match_config || null;
  const rules = useMemo(() => getSubstitutionRules(sportName, matchConfig), [sportName, matchConfig]);
  const [lineups, setLineups] = useState(EMPTY);
  const [error, setError] = useState(null);
  const lineupsRef = useRef(lineups);
  lineupsRef.current = lineups;
  const lastEventRef = useRef(null);

  const matchDbId = match?.id;
  const isLive = match?.status === 'in_progress';
  const savedLineups = (match?.matchConfig || match?.match_config)?.lineup;
  const enabled = !!active && rules.enabled;

  const persist = useCallback(async (side, lineup) => {
    if (!matchDbId) return true;
    try {
      await apiClient.put(`/matches/${matchDbId}/lineup`, { participant: side, lineup });
      return true;
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Could not save the lineup');
      return false;
    }
  }, [matchDbId]);

  // Build each side once its roster has loaded
  useEffect(() => {
    if (!enabled) return;
    const rosters = { participant1: p1Players, participant2: p2Players };
    const additions = {};
    SIDES.forEach((side) => {
      if (lineupsRef.current[side] || !rosters[side]?.length) return;
      additions[side] = reconcileLineup(savedLineups?.[side], rosters[side]) || buildInitialLineup(rosters[side]);
    });
    if (Object.keys(additions).length === 0) return;
    setLineups((prev) => {
      const next = { ...prev };
      Object.entries(additions).forEach(([side, lineup]) => { if (!next[side]) next[side] = lineup; });
      return next;
    });
  }, [enabled, p1Players, p2Players, savedLineups]);

  // Save a new starting lineup once, so the result only credits players who appeared
  const checkedSidesRef = useRef(new Set());
  useEffect(() => {
    if (!enabled || !canEdit || !isLive) return;
    SIDES.forEach((side) => {
      const lineup = lineups[side];
      if (!lineup || checkedSidesRef.current.has(side)) return;
      checkedSidesRef.current.add(side);
      if (!savedLineups?.[side] && lineup.bench.length > 0) persist(side, lineup);
    });
  }, [enabled, canEdit, isLive, lineups, savedLineups, persist]);

  // Follow lineup changes made on another device
  useEffect(() => {
    if (!enabled || !matchEvents?.length) return;
    const event = matchEvents.find((e) => (e.eventType || e.event_type || e.type) === 'lineup_update');
    if (!event) return;
    const eventKey = event.eventId || event.timestamp;
    if (eventKey && eventKey === lastEventRef.current) return;
    lastEventRef.current = eventKey;
    const data = event.eventData || event.data || {};
    if (!SIDES.includes(data.participant)) return;
    const roster = data.participant === 'participant1' ? p1Players : p2Players;
    const next = reconcileLineup(data.lineup, roster);
    if (next) setLineups((prev) => ({ ...prev, [data.participant]: next }));
  }, [enabled, matchEvents, p1Players, p2Players]);

  /** @returns {Promise<{ allowed: boolean, reason?: string, lineup?: Object }>} */
  const substitute = useCallback(async (side, outId, inId, meta = {}) => {
    const current = lineupsRef.current[side];
    const check = canSubstitute(current, outId, inId, rules);
    if (!check.allowed) return check;
    const next = applySubstitution(current, outId, inId, meta);
    setLineups((prev) => ({ ...prev, [side]: next }));
    if (await persist(side, next)) return { allowed: true, lineup: next };
    setLineups((prev) => (prev[side] === next ? { ...prev, [side]: current } : prev));
    return { allowed: false, reason: 'The substitution could not be saved' };
  }, [rules, persist]);

  /** New set: reset the per-set substitution counter (volleyball). */
  const startNewSet = useCallback((set) => {
    if (!enabled || rules.maxPerSet == null) return;
    SIDES.forEach((side) => {
      const current = lineupsRef.current[side];
      if (!current || (current.set === set && !current.subsThisSet)) return;
      const next = resetForNewSet(current, set);
      setLineups((prev) => ({ ...prev, [side]: next }));
      if (canEdit) persist(side, next);
    });
  }, [enabled, rules.maxPerSet, canEdit, persist]);

  const clearError = useCallback(() => setError(null), []);

  return {
    rules,
    lineups: enabled ? lineups : EMPTY,
    substitute,
    startNewSet,
    error,
    clearError,
  };
};

export default useLiveMatchLineup;
